import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Icon from './Icon';
import { getAccessToken, isSecretaryContextUser } from '../authStorage';
import { deleteSecretaryUser, fetchSecretaryUsers } from '../secretaryUsersApi';
import { useLanguage } from '../i18n/language';
import { useT } from '../i18n/translations';
import './UsersPage.css';

/* ── avatar initials ── */
function getInitials(name, username) {
  const src = (name || username || '?').trim();
  const parts = src.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return src.slice(0, 2).toUpperCase();
}

/* ── gradient palette by index ── */
const AVATAR_GRADIENTS = [
  ['#02c3a6', '#00796b'],
  ['#1e88e5', '#1565c0'],
  ['#8e24aa', '#6a1b9a'],
  ['#fb8c00', '#e65100'],
  ['#e53935', '#b71c1c'],
  ['#00897b', '#004d40'],
];
function avatarGradient(idx) {
  const [a, b] = AVATAR_GRADIENTS[idx % AVATAR_GRADIENTS.length];
  return `linear-gradient(135deg, ${a}, ${b})`;
}

/* ── skeleton card ── */
function SkeletonCard() {
  return (
    <div className="sec-card sec-card--skeleton">
      <div className="sec-card__skel sec-card__skel--avatar" />
      <div className="sec-card__skel sec-card__skel--name" />
      <div className="sec-card__skel sec-card__skel--meta" />
      <div className="sec-card__skel sec-card__skel--badge" />
    </div>
  );
}

/* ── main component ── */
export default function UsersPage() {
  const { direction } = useLanguage();
  const t = useT();
  const navigate = useNavigate();
  const location = useLocation();
  const basePath = location.pathname.startsWith('/officer') ? '/officer' : '/app';

  const [accessDenied, setAccessDenied] = useState(false);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState('');
  const [query, setQuery]         = useState('');
  const [users, setUsers]         = useState([]);
  const [filter, setFilter]       = useState('all'); // all | active | inactive
  const [confirmId, setConfirmId] = useState(null);
  const [deleting, setDeleting]   = useState(false);

  const loadUsers = useCallback(async () => {
    setLoading(true); setError('');
    try {
      setUsers(await fetchSecretaryUsers());
    } catch (e) {
      setError(e instanceof Error ? e.message : t('users.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!getAccessToken())       { navigate('/login', { replace: true }); return; }
    if (!isSecretaryContextUser()){ setAccessDenied(true); setLoading(false); return; }
    loadUsers();
  }, [navigate, loadUsers]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return users.filter(u => {
      if (filter === 'active'   && !u.isActive) return false;
      if (filter === 'inactive' &&  u.isActive) return false;
      if (!q) return true;
      return (
        (u.fullName   || '').toLowerCase().includes(q) ||
        (u.userName   || '').toLowerCase().includes(q) ||
        (u.email      || '').toLowerCase().includes(q) ||
        (u.phone      || '').includes(q) ||
        (u.departmentName || '').toLowerCase().includes(q)
      );
    });
  }, [users, query, filter]);

  const handleDelete = async () => {
    if (!confirmId) return;
    setDeleting(true);
    try {
      await deleteSecretaryUser(confirmId);
      setConfirmId(null);
      await loadUsers();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('users.deleteFailed'));
      setConfirmId(null);
    } finally {
      setDeleting(false);
    }
  };

  const counts = useMemo(() => ({
    all:      users.length,
    active:   users.filter(u =>  u.isActive).length,
    inactive: users.filter(u => !u.isActive).length,
  }), [users]);

  /* ── access denied ── */
  if (accessDenied) {
    return (
      <div className="sec-page" dir={direction}>
        <div className="sec-denied">
          <span className="sec-denied__icon">🔒</span>
          <p>{t('common.noPerm')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="sec-page" dir={direction}>

      {/* ── header banner ── */}
      <div className="sec-header">
        <div className="sec-header__left">
          <div className="sec-header__icon-wrap">🔐</div>
          <div>
            <h1 className="sec-header__title">{t('users.title')}</h1>
            <p  className="sec-header__sub">{t('users.subtitle')}</p>
          </div>
        </div>
        <button className="sec-header__add-btn" onClick={() => navigate(`${basePath}/secretaries/new`)}>
          {t('users.add')}
        </button>
      </div>

      {/* ── toolbar ── */}
      <div className="sec-toolbar">
        <div className="sec-toolbar__search">
          <Icon name="search" size={15} color="#90a4ae" />
          <input
            type="search"
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder={t('users.searchPh')}
            autoComplete="off"
            disabled={loading}
          />
          {query && (
            <button className="sec-toolbar__clear" onClick={() => setQuery('')}>✕</button>
          )}
        </div>

        <div className="sec-toolbar__filters">
          {['all','active','inactive'].map(f => (
            <button
              key={f}
              className={`sec-filter-btn${filter === f ? ' sec-filter-btn--active' : ''}`}
              onClick={() => setFilter(f)}
            >
              {f === 'all' ? t('common.all') : f === 'active' ? t('common.active') : t('common.inactive')}
              <span className="sec-filter-btn__count">{counts[f]}</span>
            </button>
          ))}
        </div>

        <span className="sec-toolbar__count" aria-live="polite">
          {filtered.length} {t('users.secretaryCount')}
        </span>
      </div>

      {/* ── error ── */}
      {error && <div className="sec-error">{error}</div>}

      {/* ── grid ── */}
      <div className="sec-grid">
        {loading
          ? [0,1,2,3].map(i => <SkeletonCard key={i} />)
          : filtered.length === 0
            ? (
              <div className="sec-empty">
                <span className="sec-empty__icon">👤</span>
                <p>{query.trim() ? t('users.noMatch') : t('users.noUsers')}</p>
              </div>
            )
            : filtered.map((u, idx) => (
              <div key={u.id} className={`sec-card${!u.isActive ? ' sec-card--inactive' : ''}`}>
                {/* status dot */}
                <span className={`sec-card__dot${u.isActive ? ' sec-card__dot--on' : ''}`} />

                {/* avatar + name */}
                <div className="sec-card__top">
                  <div className="sec-card__avatar" style={{ background: avatarGradient(idx) }}>
                    {getInitials(u.fullName, u.userName)}
                  </div>
                  <div className="sec-card__identity">
                    <h3 className="sec-card__name">{u.fullName?.trim() || u.userName || '—'}</h3>
                    <span className="sec-card__username" dir="ltr">@{u.userName || '—'}</span>
                  </div>
                  <span className={`sec-card__badge${u.isActive ? ' sec-card__badge--active' : ' sec-card__badge--inactive'}`}>
                    {u.isActive ? t('common.active') : t('common.inactive')}
                  </span>
                </div>

                {/* meta rows */}
                <div className="sec-card__meta">
                  {u.email && (
                    <div className="sec-card__meta-row">
                      <span className="sec-card__meta-icon">✉️</span>
                      <span className="sec-card__meta-val" dir="ltr">{u.email}</span>
                    </div>
                  )}
                  {u.phone && (
                    <div className="sec-card__meta-row">
                      <span className="sec-card__meta-icon">📱</span>
                      <span className="sec-card__meta-val" dir="ltr">{u.phone}</span>
                    </div>
                  )}
                  {u.departmentName && (
                    <div className="sec-card__meta-row">
                      <span className="sec-card__meta-icon">🏢</span>
                      <span className="sec-card__meta-val">{u.departmentName}</span>
                    </div>
                  )}
                </div>

                {/* actions */}
                <div className="sec-card__actions">
                  <button
                    className="sec-card__btn sec-card__btn--edit"
                    onClick={() => navigate(`${basePath}/secretaries/${encodeURIComponent(u.id)}/edit`)}
                  >
                    <Icon name="edit" size={13} /> {t('common.edit')}
                  </button>
                  <button
                    className="sec-card__btn sec-card__btn--delete"
                    onClick={() => setConfirmId(u.id)}
                  >
                    <Icon name="trash" size={13} /> {t('common.delete')}
                  </button>
                </div>
              </div>
            ))
        }
      </div>

      {/* ── confirm delete modal ── */}
      {confirmId && (
        <div className="sec-modal-overlay" onClick={() => setConfirmId(null)}>
          <div className="sec-modal" onClick={e => e.stopPropagation()}>
            <div className="sec-modal__icon">⚠️</div>
            <h3 className="sec-modal__title">{t('users.deleteTitle')}</h3>
            <p className="sec-modal__msg">{t('users.deleteMsg')}</p>
            <div className="sec-modal__btns">
              <button className="sec-modal__btn sec-modal__btn--cancel" onClick={() => setConfirmId(null)} disabled={deleting}>
                {t('common.cancel')}
              </button>
              <button className="sec-modal__btn sec-modal__btn--confirm" onClick={handleDelete} disabled={deleting}>
                {deleting ? '…' : t('common.delete')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
